import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  formatAud,
  paybackBullet,
  paybackHeadline,
  rebateBullet,
  rebateHeadline,
  sourceBullet,
} from "@aucash/rules/solar-battery";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { FULL_DISCLAIMER, SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// solar-battery — federal home-battery (Cheaper Home Batteries / SRES) rebate
// sizer + indicative payback, surfaced stateless-v1 (PLAN arch decision #1).
// One screen; the result renders INLINE (no save-by-id, no auth gate, no
// persistence). Each compute is a stateless `calc.solarBattery.compute` query —
// input -> output, no round-trip to a stored row. Copy comes verbatim from the
// rules layer's copy helpers so the wording can never drift from the API/SEO
// surfaces (validation §3: information, not advice).
//
// Regulatory posture (binding): the rebate dollar figure is INDICATIVE, "based
// on an STC price of $X" — never a locked quote. Payback is cited arithmetic
// returned as a RANGE with its assumptions surfaced — never "you'll save $X/yr"
// / "buy a battery". The solar-installer affiliate handoff stays OFF this
// surface entirely (facts only). The rebate schedule is DATE-keyed: the deeming
// factor steps down twice-yearly, so the install-date window selects the
// schedule — the UI never hard-codes a figure.

// The two registered CER schedules (date-keyed; the deeming factor steps down
// 8.4 → 6.8 from 1 May 2026). Each window maps to a representative install date
// the API resolves the schedule from — never Date.now(). The current window is
// listed first (the default).
type InstallWindow = "from-may-2026" | "jan-apr-2026";

const INSTALL_WINDOW_OPTIONS: ReadonlyArray<{
  value: InstallWindow;
  label: string;
}> = [
  { value: "from-may-2026", label: "From 1 May 2026" },
  { value: "jan-apr-2026", label: "Jan–Apr 2026" },
];

/** Representative install date for each window (selects the dated schedule). */
const WINDOW_DATE: Record<InstallWindow, string> = {
  "from-may-2026": "2026-06-01",
  "jan-apr-2026": "2026-03-15",
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.solarBattery.compute.query>
>;

export default function SolarBatteryIndex(): JSX.Element {
  const router = useRouter();

  const [installWindow, setInstallWindow] = useState<InstallWindow>("from-may-2026");
  const [usableKwh, setUsableKwh] = useState("");
  const [meetsCoreEligibility, setMeetsCoreEligibility] = useState(true);

  // Optional payback inputs.
  const [installCost, setInstallCost] = useState("");
  const [annualSavingLow, setAnnualSavingLow] = useState("");
  const [annualSavingHigh, setAnnualSavingHigh] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onWindowChange = (next: InstallWindow): void => {
    setInstallWindow(next);
    setResult(null);
    setSubmitError(null);
  };

  const buildInput = ():
    | { input: Parameters<typeof trpc.calc.solarBattery.compute.query>[0]["input"] }
    | null => {
    const kwh = num(usableKwh);
    if (kwh <= 0) return null;

    // Payback is only computed when the user supplies an install cost AND a
    // saving range; otherwise we surface the rebate facts only.
    const cost = num(installCost);
    const low = num(annualSavingLow);
    const high = num(annualSavingHigh);
    const wantsPayback =
      installCost.trim() !== "" && cost > 0 &&
      (annualSavingLow.trim() !== "" || annualSavingHigh.trim() !== "");

    return {
      input: {
        installDate: WINDOW_DATE[installWindow],
        rebate: {
          usableKwh: kwh,
          meetsCoreEligibility,
        },
        ...(wantsPayback
          ? {
              payback: {
                installCostAud: cost,
                annualSavingLowAud: low,
                annualSavingHighAud: high,
              },
            }
          : {}),
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (usableKwh.trim() === "")
      next.kwh = "Enter the battery's usable capacity in kWh.";
    if (installCost.trim() !== "") {
      if (annualSavingLow.trim() === "" && annualSavingHigh.trim() === "")
        next.saving =
          "To see an indicative payback range, enter a low and high annual bill saving.";
    }
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.solarBattery.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Home battery rebate" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>Cheaper Home Batteries · SRES</Eyebrow>
            <Text style={styles.title}>
              What's the federal discount on a home battery?
            </Text>
            <Text style={styles.subtitle}>
              The Cheaper Home Batteries program delivers a federal discount as
              Small-scale Technology Certificates (STCs). See the indicative
              discount your accredited installer should apply — sized from your
              usable capacity and the Clean Energy Regulator's deeming factor —
              and an indicative simple-payback range. The STC price floats, so
              dollar figures are indicative, not a quote.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="When is (or was) the battery installed?"
            help="The federal deeming factor steps down twice a year, so the install date sets the schedule. From 1 May 2026 it is 6.8 STCs per usable kWh (down from 8.4)."
          >
            <Segmented<InstallWindow>
              options={INSTALL_WINDOW_OPTIONS}
              value={installWindow}
              onChange={onWindowChange}
              testIDPrefix="install-window"
            />
          </Field>

          <Field
            label="Usable battery capacity (kWh)"
            help="The usable capacity the rebate is sized on — typically a little below the nameplate (nominal) figure. The program covers from 5 kWh usable."
            error={errors.kwh}
          >
            <MoneyInput
              value={usableKwh}
              onChange={setUsableKwh}
              testID="input-usable-kwh"
              accessibilityLabel="Usable battery capacity in kilowatt hours"
            />
          </Field>

          <Field label="Does the install meet the program's core eligibility?">
            <Checkbox
              checked={meetsCoreEligibility}
              onChange={setMeetsCoreEligibility}
              label="Yes — a single job with new or existing rooftop solar, a CER-approved product, and (if on-grid) VPP-capable. Your installer confirms eligibility."
            />
          </Field>

          <View style={styles.optionalNote}>
            <Text style={styles.optionalNoteText}>
              Optional — add an install cost and a low/high annual bill saving to
              see an indicative simple-payback RANGE. The saving range is yours to
              estimate; we only do the arithmetic and surface the assumptions.
            </Text>
          </View>

          <Field
            label="Total installed cost before the federal discount (optional)"
            help="The installer's price for the battery and installation, before the federal STC discount is applied."
          >
            <MoneyInput
              value={installCost}
              onChange={setInstallCost}
              testID="input-install-cost"
              accessibilityLabel="Total installed cost before the federal discount"
            />
          </Field>

          <Field
            label="Annual bill saving — low estimate (optional)"
            help="Your conservative estimate of the yearly bill saving (self-consumption shifted off-peak, avoided peak import, less any feed-in-tariff change)."
            error={errors.saving}
          >
            <MoneyInput
              value={annualSavingLow}
              onChange={setAnnualSavingLow}
              testID="input-saving-low"
              accessibilityLabel="Annual bill saving low estimate"
            />
          </Field>

          <Field label="Annual bill saving — high estimate (optional)">
            <MoneyInput
              value={annualSavingHigh}
              onChange={setAnnualSavingHigh}
              testID="input-saving-high"
              accessibilityLabel="Annual bill saving high estimate"
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See the indicative discount →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="solar-battery-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer sits at the TOP, before any number (§3.2 placement
 * rule). All wording comes from the rules-layer copy helpers — the rebate is
 * "indicative, based on an STC price of $X", the payback is a RANGE + its
 * assumptions; no advice framing, no installer handoff. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { rebate, payback } = result.output;

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FULL_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · Cheaper Home Batteries</Eyebrow>
        <Chip label="Indicative" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{rebateHeadline(rebate)}</Text>
        <Text style={styles.bodyText}>{rebateBullet(rebate)}</Text>
      </Card>

      {payback !== null ? (
        <Card style={{ gap: space.sm }}>
          <Text style={styles.lead}>{paybackHeadline(payback)}</Text>
          <Text style={styles.bodyText}>{paybackBullet(payback)}</Text>
          <Section title="The assumptions behind this range">
            {payback.assumptions.map((a) => (
              <Text key={a} style={styles.bodyText}>
                • {a}
              </Text>
            ))}
          </Section>
        </Card>
      ) : null}

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.scheduleKey, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · schedule{" "}
          {result.scheduleKey} · ruleset {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline strings already embed the formatted figures, so it isn't called
// directly here. Referenced to keep the import meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  optionalNote: {
    backgroundColor: colors.sand,
    borderRadius: radius.md,
    padding: space.md,
  },
  optionalNoteText: {
    fontFamily: fonts.body,
    fontSize: 13,
    lineHeight: 19,
    color: colors.inkSoft,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
